import { useMemo, useContext, createContext, useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../../redux/hooks'
import companyPlanningRecordLettersActions from '../../../redux/modules/companyPlanningRecordLetters/actions'
import companyPlanningRecordLettersSelectors from '../../../redux/modules/companyPlanningRecordLetters/selectors'
import authSelectors from '../../../redux/modules/auth/selectors'
import { useStateWithRef } from '../../../hooks/shared/useStateWithRef'
import { useUnsubscribe } from '../../../hooks/shared/useUnsubscribe'
import companyPlanningRecordLetterService from '../../../services/companyPlanningRecordLetterService'
import { CompanyPlanningRecordLetter } from '@leadgen/models/client'
import { useRegisteredComponents } from '../../../hooks/shared/useRegisteredComponents'
import useStableCallback from '../../../hooks/shared/useStableCallback'

export type CompanyPlanningRecordLettersStatus =
  | 'reloading-initial'
  | 'loading-initial'
  | 'loading-more'
  | 'idle'
  | 'empty'
  | 'error-initial'
  | 'error-more'

const CompanyPlanningRecordLettersContext = createContext<{
  registerComponent: () => () => void
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadFresh: () => void
  loadMore: () => void
  status: CompanyPlanningRecordLettersStatus
  companyPlanningRecordLetters: CompanyPlanningRecordLetter[]
  error: string | undefined
} | null>(null)

const useCompanyPlanningRecordLetters = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(CompanyPlanningRecordLettersContext)
  if (!context) {
    throw new Error(
      'no CompanyPlanningRecordLettersProvider found when calling useCompanyPlanningRecordLetters()',
    )
  }
  const {
    registerComponent,
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    companyPlanningRecordLetters,
    error,
  } = context
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    companyPlanningRecordLetters,
    error,
  }
}

const useCompanyPlanningRecordLettersPreview = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(CompanyPlanningRecordLettersContext)
  if (!context) {
    throw new Error(
      'no CompanyPlanningRecordLettersProvider found when calling useCompanyPlanningRecordLettersPreview()',
    )
  }
  const { registerComponent, loading, status, companyPlanningRecordLetters, error } = context
  const topThreeCompanyPlanningRecordLetters = useMemo(() => {
    return companyPlanningRecordLetters?.slice(0, 3)
  }, [companyPlanningRecordLetters])
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    companyPlanningRecordLetters: topThreeCompanyPlanningRecordLetters,
    status,
    loading,
    error,
  }
}

const CompanyPlanningRecordLettersProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const { registerComponent, hasRegisteredComponents, registeredComponentsRef } =
    useRegisteredComponents()
  const [hasMore, setHasMore, hasMoreRef] = useStateWithRef(false)
  const companyPlanningRecordLetters = useSelector(
    companyPlanningRecordLettersSelectors.getCompanyPlanningRecordLetters,
  )
  const [loading, loadingRef] = useSelectorWithRef(
    companyPlanningRecordLettersSelectors.getLoading(),
  )
  const [loadingMore, loadingMoreRef] = useSelectorWithRef(
    companyPlanningRecordLettersSelectors.getLoadingMore,
  )
  const [, initialRef] = useSelectorWithRef(companyPlanningRecordLettersSelectors.getInitial)
  const error = useSelector(companyPlanningRecordLettersSelectors.getError)
  const companyId = useSelector(authSelectors.getCompanyId)
  const status: CompanyPlanningRecordLettersStatus =
    loading && companyPlanningRecordLetters.length
      ? 'reloading-initial'
      : loading
        ? 'loading-initial'
        : loadingMore
          ? 'loading-more'
          : !loading && !loadingMore && error && !companyPlanningRecordLetters.length
            ? 'error-initial'
            : !loading && !loadingMore && error && companyPlanningRecordLetters.length
              ? 'error-more'
              : !loading && !loadingMore && !error && !companyPlanningRecordLetters.length
                ? 'empty'
                : 'idle'
  const loadFresh = useCallback(() => {
    if (
      companyId &&
      hasRegisteredComponents &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(
        companyPlanningRecordLettersActions.loadCompanyPlanningRecordLettersFresh({ companyId }),
      )
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromCompanyPlanningRecordLetters }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromCompanyPlanningRecordLetters()
            unsubscribe()
          } else {
            setHasMore(data?.length === companyPlanningRecordLetterService.pageSize)
            unsubscribe()
            registerUnsubscribe(unsubscribeFromCompanyPlanningRecordLetters)
          }
        })
    }
  }, [
    companyId,
    dispatch,
    hasRegisteredComponents,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  const loadMore = useCallback(() => {
    if (
      companyId &&
      hasMoreRef.current &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(
        companyPlanningRecordLettersActions.loadCompanyPlanningRecordLettersNext({ companyId }),
      )
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromCompanyPlanningRecordLetters }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromCompanyPlanningRecordLetters()
            unsubscribe()
          } else {
            setHasMore(data?.length === companyPlanningRecordLetterService.pageSize)
            registerUnsubscribe(unsubscribeFromCompanyPlanningRecordLetters)
          }
        })
    }
  }, [
    companyId,
    dispatch,
    hasMoreRef,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  useEffect(() => {
    loadFresh()
  }, [loadFresh])
  useEffect(() => {
    if (!hasRegisteredComponents) {
      unsubscribe()
      return
    }
  }, [hasRegisteredComponents, unsubscribe])
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
  const loadFreshHandler = useStableCallback(loadFresh)
  const loadMoreHandler = useStableCallback(loadMore)
  const value = useMemo(
    () => ({
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFresh: loadFreshHandler,
      loadMore: loadMoreHandler,
      status,
      companyPlanningRecordLetters,
      error,
    }),
    [
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFreshHandler,
      loadMoreHandler,
      status,
      companyPlanningRecordLetters,
      error,
    ],
  )
  return (
    <CompanyPlanningRecordLettersContext.Provider value={value}>
      {children}
    </CompanyPlanningRecordLettersContext.Provider>
  )
}

export {
  useCompanyPlanningRecordLetters,
  useCompanyPlanningRecordLettersPreview,
  CompanyPlanningRecordLettersProvider,
}
export default CompanyPlanningRecordLettersProvider
